import { useCallback, useEffect, useState } from 'react'
import { CaseNotesListView } from '@/components/case-notes/CaseNotesListView'
import { api } from '@/services/api'
import type { GraphNoteOrFinding } from '@/services/api/graph'
import type { CaseNote } from '@/services/api/notes'

interface CaseNotesListProps {
  caseId: string
  currentUserName?: string
}

/**
 * Connected case-notes panel. Owns both data sources — PostgreSQL case
 * notes (CRUD) and read-only Neo4j graph notes/findings — and the add /
 * update / delete persistence, feeding the pure {@link CaseNotesListView}.
 */
export function CaseNotesList({ caseId, currentUserName = 'Analyst' }: CaseNotesListProps) {
  const [notes, setNotes] = useState<CaseNote[]>([])
  const [graphItems, setGraphItems] = useState<GraphNoteOrFinding[]>([])
  const [loading, setLoading] = useState(true)

  // Fetch notes from database
  const fetchNotes = useCallback(async () => {
    try {
      const data = await api.notes.list(caseId)
      setNotes(data)
    } catch (error) {
      console.error('Failed to fetch notes:', error)
    }
  }, [caseId])

  // Fetch notes and findings from graph
  const fetchGraphItems = useCallback(async () => {
    try {
      const response = await api.graph.getNotesAndFindings(caseId)
      setGraphItems(response.items)
    } catch (error) {
      console.error('Failed to fetch graph notes/findings:', error)
    }
  }, [caseId])

  // Fetch both on mount
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true)
      await Promise.all([fetchNotes(), fetchGraphItems()])
      setLoading(false)
    }
    fetchAll()
  }, [fetchNotes, fetchGraphItems])

  // Add new note
  const handleAddNote = useCallback(
    async (content: string) => {
      try {
        const newNote = await api.notes.create(caseId, {
          content,
          author_name: currentUserName,
        })
        setNotes(prev => [newNote, ...prev])
      } catch (error) {
        console.error('Failed to add note:', error)
      }
    },
    [caseId, currentUserName]
  )

  // Update note
  const handleUpdateNote = useCallback(
    async (noteId: string, content: string) => {
      try {
        const updatedNote = await api.notes.update(caseId, noteId, { content })
        setNotes(prev => prev.map(n => (n.id === noteId ? updatedNote : n)))
      } catch (error) {
        console.error('Failed to update note:', error)
      }
    },
    [caseId]
  )

  // Delete note
  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      try {
        await api.notes.delete(caseId, noteId)
        setNotes(prev => prev.filter(n => n.id !== noteId))
      } catch (error) {
        console.error('Failed to delete note:', error)
      }
    },
    [caseId]
  )

  return (
    <CaseNotesListView
      notes={notes}
      graphItems={graphItems}
      loading={loading}
      currentUserName={currentUserName}
      onAddNote={handleAddNote}
      onUpdateNote={handleUpdateNote}
      onDeleteNote={handleDeleteNote}
    />
  )
}
